import { getApiEndpoints } from '@/services/api/endpoints';
import { createAppAsyncThunk } from '@/store/typedAsyncThunk';
import type {
  HomeownerPoolListEnvelope,
  HomeownerPoolLookupEnvelope,
  HomeownerPoolRejectedValue,
  ListHomeownerPoolUsersArgs,
} from './homeownerUsersPoolTypes';

const DEFAULT_LIMIT = 25;
const MIN_LIMIT = 1;
const MAX_LIMIT = 50;

function normalizeLimit(limit?: number): number {
  if (typeof limit !== 'number' || !Number.isFinite(limit)) return DEFAULT_LIMIT;
  return Math.min(MAX_LIMIT, Math.max(MIN_LIMIT, Math.trunc(limit)));
}

function mapError(error: unknown): HomeownerPoolRejectedValue {
  const status = (error as { status?: number })?.status;

  if (status === 400) {
    return { code: 'invalid-search', message: 'That homeowner identity search is not valid.' };
  }
  if (status === 401 || status === 403) {
    return { code: 'access-denied', message: 'You do not have access to homeowner authentication identities.' };
  }
  if (status === 404) {
    return { code: 'not-found', message: 'No homeowner authentication identity was found.' };
  }
  return { code: 'unavailable', message: 'Homeowner authentication identities are temporarily unavailable.' };
}

export const listHomeownerPoolUsers = createAppAsyncThunk<HomeownerPoolListEnvelope, ListHomeownerPoolUsersArgs | void>(
  'homeownerUsersPool/list',
  async (arg, { extra: { config }, rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ limit: String(normalizeLimit(arg?.limit)) });
      if (arg?.nextToken) params.set('nextToken', arg.nextToken);
      if (arg?.query) params.set('query', arg.query);

      return await config.apiService.get<HomeownerPoolListEnvelope>(
        `${getApiEndpoints().authAdmin.listHomeownerUsers}?${params.toString()}`,
        { suppressLogging: true },
      );
    } catch (error) {
      return rejectWithValue(mapError(error));
    }
  },
  {
    condition: (_, { getState }) => !getState().homeownerUsersPool.isLoading,
  },
);

export const findHomeownerPoolUserBySub = createAppAsyncThunk<HomeownerPoolLookupEnvelope, string>(
  'homeownerUsersPool/findBySub',
  async (sub, { extra: { config }, rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ sub });
      return await config.apiService.get<HomeownerPoolLookupEnvelope>(
        `${getApiEndpoints().authAdmin.listHomeownerUsers}?${params.toString()}`,
        { suppressLogging: true },
      );
    } catch (error) {
      return rejectWithValue(mapError(error));
    }
  },
  {
    condition: (_, { getState }) => !getState().homeownerUsersPool.isLoading,
  },
);
